<template>
    <div class="apprentice-simple-card">
        <design-2021-apprentice-data-table
            class="periods-overview-table"
            :items="tableData"
            :fields="periodFields"
            :show-empty="true">
            <template v-slot:options>
                <div class="seasons">
                    <b-form-select
                        class="apprentice-primary-select season-select"
                        id="season-select"
                        v-if="seasons.length"
                        :value="season"
                        :options="seasons"
                        text-field="season"
                        value-field="id"
                        @change="seasonChanged"
                        :disabled="seasons.length === 2">
                    </b-form-select>
                </div>
                <b-button
                    class="btn-add btn-add-period"
                    v-b-modal="'create-period'"
                    type="button"
                    variant="primary"
                    :label="$t('periods.add_period')">
                    {{ $t('periods.add_period') }}
                </b-button>
            </template>
            <template v-slot:cell(action)="data">
                <div class="management-actions">
                    <b-button
                        class="btn-edit btn-edit-internship btn-"
                        v-b-tooltip.hover
                        variant="primary-icon-only"
                        :title="$t('common.edit')"
                        @click="editPeriod(data.item.id)">
                    </b-button>
                    <b-button
                        class="btn-remove btn-remove-internship"
                        v-b-tooltip.hover
                        variant="danger-icon-only"
                        :title="$t('common.delete')"
                        @click="removePeriod(data.item.id)">
                    </b-button>
                </div>
            </template>
            <template #empty="scope">
                <div class="not-found with-header attachments-list">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('periods.no_periods_found') }}</div>
                </div>
            </template>
        </design-2021-apprentice-data-table>

        <design-2021-period-create-modal-component
            ref="createPeriod"
            @add-period="addPeriod">
        </design-2021-period-create-modal-component>

        <design-2021-period-edit-modal-component
            ref="edit-period"
            @update-period="updatePeriod($event)">
        </design-2021-period-edit-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-current-periods-index-component',
    props: ['allPeriodsIn', 'currentPeriodsIn', 'seasonsIn', 'currentSeasonIdIn'],
    data() {
        return {
            currentPeriods: this.currentPeriodsIn,
            season: 0,
            seasons: [],
            periods: this.allPeriodsIn,
            periodFields: [
                {
                    key: 'title',
                    label: this.$t('periods.period'),
                },
                {
                    key: 'submissionOpening',
                    label: this.$t('periods.registration_opening'),
                },
                {
                    key: 'submissionDeadline',
                    label: this.$t('periods.registration_deadline'),
                },
                {
                    key: 'startDate',
                    label: this.$t('periods.start_date'),
                },
                {
                    key: 'endDate',
                    label: this.$t('periods.end_date'),
                },
                {
                    key: 'action',
                    label: this.$t('common.actions'),
                },
            ],
        };
    },
    mounted() {
        let seasonsWithPeriods = this.seasonsIn
            .filter((season) => this.allPeriodsIn.findIndex((period) => period.season_id === season.id) !== -1)
            .map((season) => {
                if (season.id !== this.currentSeasonIdIn) {
                    return season;
                }
                let currentSeason = { ...season };
                currentSeason.season = this.$t('periods.current_school_year');
                return currentSeason;
            });

        seasonsWithPeriods.push({
            id: 0,
            season: this.$t('periods.all_school_years'),
        });

        if (seasonsWithPeriods.length) {
            this.seasons = seasonsWithPeriods.reverse();
            const key = 'period-overview-season-' + this.$authUser.id;
            const seasonStored = parseInt(localStorage.getItem(key));
            if (seasonStored && this.seasonsIn.some((season) => season.id === seasonStored)) {
                this.season = seasonStored;
            } else if (this.currentSeasonId && this.seasonsIn.some((season) => season.id === this.currentSeasonId)) {
                this.season = this.currentSeasonId;
            }
        }
    },
    computed: {
        tableData() {
            let items = [];
            if (this.periods.length) {
                items = this.periods.map((period) => {
                    let seasonYear = '';

                    let seasonIndex = this.seasons.findIndex((season) => season.id === period.season_id);
                    if (seasonIndex >= 0) {
                        seasonYear = this.seasons[seasonIndex].season;
                    }

                    period.startDateUnix = Date.parse(period.start_date);
                    period.startDate = this.formatDate(period.start_date);
                    period.endDate = this.formatDate(period.end_date);
                    period.submissionDeadline = this.formatDate(period.sign_up_until_date);
                    period.submissionOpening = this.formatDate(period.sign_up_from_date);
                    return period;
                });

                if (this.season !== 0) {
                    items = items.filter((item) => {
                        if (item.season_id === this.season) {
                            return true;
                        }
                    });
                }
            }

            return items.sort((a, b) => parseFloat(a.startDateUnix) - parseFloat(b.startDateUnix));
        },
    },
    methods: {
        addPeriod(period) {
            this.periods.push(period);
            const seasonAdded = this.seasonsIn.find((season) => season.id === period.season_id);
            this.seasons.push(seasonAdded);
        },

        updatePeriod(period) {
            this.periods = this.periods.map((period2) => {
                if (period2.id === period.id) {
                    return period;
                }
                return period2;
            });
        },

        seasonChanged(value) {
            const key = 'period-overview-season-' + this.$authUser.id;
            localStorage.setItem(key, value);
            this.season = value;
        },

        editPeriod(periodId) {
            const period = this.periods.find((period) => period.id === periodId);
            this.$refs['edit-period'].show(period);
        },

        removePeriod(periodId) {
            this.alert
                .confirm(
                    this.$t('periods.delete_period'),
                    this.$t('periods.are_you_sure_you_want_to_delete_this_period'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.axios.delete(this.routes.managePeriodsDelete(periodId)).then(
                        (response) => {
                            this.periods = this.periods.filter((period) => period.id !== periodId);
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },

        formatDate(inputDate) {
            const dateParts = inputDate.split(' ')[0].split('-');
            const year = dateParts[0];
            const month = dateParts[1];
            const day = dateParts[2];
            return `${day}-${month}-${year}`;
        },
    },
};
</script>

<style scoped lang="scss">
select {
    margin-bottom: 0;
}

.periods-overview-table ::v-deep {
    .apprentice-data-table-actions.options {
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
    }
}
</style>
